/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { NETGEM_API_V8_METADATA_PROGRAM } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';

import { getChannelName, getChannelNumber } from '../../../helpers/channel/helper';
import Localizer from '../../../helpers/localization';
import { hasRunningSeriesScheduledRecording } from '../../../helpers/npvr/scheduledRecording';
import { BroadcastStatus } from '../../../helpers/ui/location/Format';
import { ItemContent, ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { RecordingOutcome } from '../../../libs/netgemLibrary/v8/types/Npvr';
import { TileOnFocus } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { PictoClock, PictoSeries } from '../../pictos/Element';
import './ItemDecoration.css';
import ProgressBar from '../../progressBar/ProgressBar';
import {
  getRecordingOutcomes,
  getSingleScheduledRecording,
  renderChannelImage,
  renderDebugInfoElement,
  renderLiveRecordingIcon,
  renderRecordingIcon,
  renderScheduledRecordingIcon,
  renderWatchingStatus,
} from './ItemDecorationHelper';

type ItemDecorationPropType = {
  +broadcastStatus: BroadcastStatus,
  +channelImageUrl: string | null,
  +contentType: ItemContent,
  +isDebugModePlusEnabled: boolean,
  +isInLiveSection?: boolean,
  +item: NETGEM_API_V8_FEED_ITEM,
  +now: number,
  +onFocus: TileOnFocus,
  +previewCatchupScheduledEventId: ?string,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +tvLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  +vodLocationsMetadata: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null,
  +watchingStatus: number | null,
};

const ItemDecoration = ({
  broadcastStatus,
  channelImageUrl,
  contentType,
  isDebugModePlusEnabled,
  isInLiveSection = false,
  item,
  now,
  onFocus,
  previewCatchupScheduledEventId,
  programMetadata,
  tvLocationMetadata,
  vodLocationsMetadata,
  watchingStatus,
}: ItemDecorationPropType): React.Node => {
  const npvrScheduledRecordingsList = useSelector((state) => state.npvr.npvrScheduledRecordingsList);
  const npvrRecordingsFuture = useSelector((state) => state.npvr.npvrRecordingsFuture);
  const npvrRecordingsList = useSelector((state) => state.npvr.npvrRecordingsList);
  const channels = useSelector((state) => state.appConfiguration.deviceChannels);

  if (onFocus === TileOnFocus.Selection) {
    return null;
  }

  const {
    selectedLocation: { channelId },
  } = item;
  const channelNumber = useMemo(() => getChannelNumber(channels, channelId), [channels, item]);
  const channelName = useMemo(() => getChannelName(channels, channelId), [channels, item]);

  const renderRegularTileDecoration = useCallback((): React.Node => {
    const {
      locType,
      selectedLocation: { id: scheduledEventId, recordOutcome },
      selectedProgramId,
      seriesId,
      type: itemType,
    } = item;

    const localScheduledEventId = previewCatchupScheduledEventId || scheduledEventId;

    if (!localScheduledEventId) {
      return null;
    }

    const hasSeriesScheduledRecording = hasRunningSeriesScheduledRecording(seriesId ?? undefined, npvrScheduledRecordingsList);

    const singleScheduledRecording = getSingleScheduledRecording(
      npvrScheduledRecordingsList,
      npvrRecordingsList,
      hasSeriesScheduledRecording,
      broadcastStatus,
      selectedProgramId,
      localScheduledEventId,
    );

    const hasSingleScheduledRecording =
      // Item is a recording
      (!hasSeriesScheduledRecording && locType === ItemLocationKind.Recording) ||
      // Item is a scheduled event, a preview or a catchup (keep from replay), and a scheduled recording of type Single exists
      ((locType === ItemLocationKind.ScheduledEvent || broadcastStatus === BroadcastStatus.Preview || broadcastStatus === BroadcastStatus.Past) && singleScheduledRecording !== undefined);

    const seriesScheduledRecording = seriesId ? npvrScheduledRecordingsList[seriesId] : null;
    const recordingOutcomes = getRecordingOutcomes(npvrRecordingsList, npvrRecordingsFuture, seriesScheduledRecording, singleScheduledRecording, item, localScheduledEventId, tvLocationMetadata);
    const goodRecordOutcome = recordingOutcomes.find((outcome) => outcome === RecordingOutcome.Recorded);

    const seriesIconElement = itemType === ItemType.Series && (contentType === ItemContent.NetgemSVOD || contentType === ItemContent.TV) ? <PictoSeries aria-hidden className='series' /> : null;

    const previewIconElement =
      broadcastStatus === BroadcastStatus.Preview ? <PictoClock aria-label={Localizer.localize('accessibility.navigation.item_preview')} className='preview' hasBackground /> : null;

    const liveRecordingIconElement = renderLiveRecordingIcon(locType, broadcastStatus, goodRecordOutcome !== undefined);

    const recordingIconElement = liveRecordingIconElement ? null : renderRecordingIcon(locType, recordOutcome ?? goodRecordOutcome);

    const scheduledRecordingIconElement =
      recordingIconElement || liveRecordingIconElement ? null : renderScheduledRecordingIcon(broadcastStatus, hasSeriesScheduledRecording, hasSingleScheduledRecording);

    const futureIconElement =
      broadcastStatus === BroadcastStatus.Future && !scheduledRecordingIconElement ? (
        <PictoClock aria-label={Localizer.localize('accessibility.navigation.item_future')} className='future' hasBackground />
      ) : null;

    return (
      <>
        {futureIconElement}
        {previewIconElement}
        {seriesIconElement}
        {recordingIconElement}
        {liveRecordingIconElement}
        {scheduledRecordingIconElement}
        {renderChannelImage(channelImageUrl, channelName)}
        {renderWatchingStatus(itemType, contentType, vodLocationsMetadata, watchingStatus)}
        <ProgressBar broadcastStatus={broadcastStatus} item={item} now={now} />
        {renderDebugInfoElement(item, hasSingleScheduledRecording, hasSeriesScheduledRecording, recordingOutcomes, channelNumber, isDebugModePlusEnabled)}
      </>
    );
  }, [
    broadcastStatus,
    channelImageUrl,
    channelNumber,
    contentType,
    isDebugModePlusEnabled,
    item,
    now,
    npvrRecordingsFuture,
    npvrRecordingsList,
    npvrScheduledRecordingsList,
    previewCatchupScheduledEventId,
    tvLocationMetadata,
    vodLocationsMetadata,
    watchingStatus,
  ]);

  // Second case is a live program without info
  return (
    <div className={clsx('decoration', channelImageUrl && 'tv', broadcastStatus === BroadcastStatus.Live && 'live')}>
      {programMetadata || !isInLiveSection ? renderRegularTileDecoration() : renderChannelImage(channelImageUrl, channelName)}
    </div>
  );
};

export default ItemDecoration;
